import React from 'react'
import { ElementClicksTag } from './ElementClickTag'

import {
  ClickSvg,
  DoubleCheckSvg,
  EyeSvg
} from '../../components/Icons'

const BUTTON_ELEMENT = 'button'
const SUGGESTION_ELEMENT = 'suggestion'

const getClickActions = (elementInfo) => {
  const clickActions = []
  
  // Handle buttons
  if (elementInfo?.buttons && Array.isArray(elementInfo.buttons)) {
    elementInfo.buttons.forEach((button, index) => {
      if (button && (button.text || button.displayText)) {
        clickActions.push(
          <ElementClicksTag 
            key={`button-${index}`}
            elementInfo={button} 
            elementType={BUTTON_ELEMENT} 
          />
        )
      }
    })
  }

  // Handle suggestions
  if (elementInfo?.suggestions && Array.isArray(elementInfo.suggestions)) {
    elementInfo.suggestions.forEach((suggestion, index) => {
      // Handle both object format {text, clicks} and string format
      let suggestionData
      if (typeof suggestion === 'string') {
        suggestionData = { text: suggestion, clicks: 0 }
      } else if (suggestion) {
        // Try multiple text fields
        let text = suggestion.text || 
                   suggestion.displayText || 
                   suggestion.reply?.text || 
                   suggestion.action?.text ||
                   suggestion.reply?.displayText ||
                   suggestion.action?.displayText ||
                   ''
        
        // If no text, try to extract from postback
        if (!text || text.trim() === '') {
          if (suggestion.postback) {
            // Try to extract meaningful text from postback
            const postbackParts = suggestion.postback.split('-')
            if (postbackParts.length > 1) {
              text = postbackParts.slice(1).join(' ').replace(/_/g, ' ').trim()
            } else {
              text = suggestion.postback.replace(/_/g, ' ').trim()
            }
          }
          
          // If still no text after postback extraction, use fallback
          if (!text || text.trim() === '') {
            text = `Option ${index + 1}`
            console.warn(`[BasicStatsLayout] Suggestion ${index} has no text, using fallback:`, text, suggestion)
          }
        }
        
        suggestionData = {
          text: text,
          clicks: suggestion.clicks || 0,
          postback: suggestion.postback,
          openUrl: suggestion.openUrl,
          type: suggestion.type
        }
      }
      
      if (suggestionData && suggestionData.text) {
        clickActions.push(
          <ElementClicksTag
            key={`suggestion-${index}`}
            elementInfo={suggestionData}
            elementType={SUGGESTION_ELEMENT}
          />
        )
      }
    })
  }
  
  return clickActions
}

export const BasicStatsLayout = ({ children, elementInfo, onNavigateToStep }) => {
  // Calculate total clicks from all options/suggestions/buttons
  const calculateTotalClicks = () => {
    let total = 0
    
    // Sum clicks from suggestions
    if (elementInfo?.suggestions && Array.isArray(elementInfo.suggestions)) {
      elementInfo.suggestions.forEach(suggestion => {
        if (suggestion && typeof suggestion === 'object') {
          total += suggestion.clicks || 0
        }
      })
    }
    
    // Sum clicks from buttons
    if (elementInfo?.buttons && Array.isArray(elementInfo.buttons)) {
      elementInfo.buttons.forEach(button => {
        if (button && typeof button === 'object') {
          total += button.clicks || 0
        }
      })
    }
    
    // For carousel, also check cards buttons
    if (elementInfo?.type === 'carousel' && elementInfo?.cards && Array.isArray(elementInfo.cards)) {
      elementInfo.cards.forEach(card => {
        if (card?.buttons && Array.isArray(card.buttons)) {
          card.buttons.forEach(button => {
            if (button && typeof button === 'object') {
              total += button.clicks || 0
            }
          })
        }
      })
    }
    
    // If elementInfo has totalClicks, use it (for carousel type)
    if (elementInfo?.totalClicks !== undefined && elementInfo?.totalClicks !== null) {
      return elementInfo.totalClicks
    }
    
    return total
  }
  
  const totalClicks = calculateTotalClicks()
  
  return (
    <div
      id="elementInfo"
      className="flex flex-col w-full"
    >
      {/* Compact stats header - above content */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
        <div className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5">
          <DoubleCheckSvg className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-semibold text-indigo-700">
            Deliveries
          </span>
          <span className="text-sm font-bold text-indigo-700 tabular-nums">
            {elementInfo?.delivered ?? 0}
          </span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5">
          <EyeSvg className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-semibold text-indigo-700">
            Visualizations
          </span>
          <span className="text-sm font-bold text-indigo-700 tabular-nums">
            {elementInfo?.displayed ?? 0}
          </span>
        </div>
        {/* Total Clicks from all options */}
        <div className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5">
          <ClickSvg className="w-4 h-4 text-indigo-600" />
          <span className="text-xs font-semibold text-indigo-700">
            Total Clicks
          </span>
          <span className="text-sm font-bold text-indigo-700 tabular-nums">
            {totalClicks}
          </span>
        </div>
      </div>
      <section id="leftColumn" className="w-full">
        {React.cloneElement(children, { onNavigateToStep })}
      </section>
    </div>
  )
}
